import { Error } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import {
  AddDetailParticipantDialog,
  CountdownTimer,
  EventInfoFragment,
  FormTextField,
  RaceOrRacepackInfo,
  RacepacksFragments,
  TicketSummaryDialog,
} from '..';
import { LoadingButton } from '@mui/lab';
import api from '../../services/apiClient';
// import { useGetEventInformationQuery } from '../../services/iracedb';
import { toCurrencyForm } from '../../utils/stringFormatter';
import CustomTabPanel, { a11lyProps } from '../CustomTabPanel/CustomTabPanel';
import classes from './style';
import prodigiLogo from '../../assets/Color horizontal.png';
import { generate } from '../../utils/generateToken';
import { useNavigate } from 'react-router-dom';
import RegCompletePopUp from '../RegCompletePopup/RegCompletePopUp';

const cacheKey = (id) => `offlane_event_${id}`;

function readCache(id) {
  try {
    const raw = localStorage.getItem(cacheKey(id));

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeCache(id, data) {
  try {
    localStorage.setItem(
      cacheKey(id),
      JSON.stringify({
        ts: Date.now(),
        data,
      }),
    );
  } catch {
    // Cache tidak wajib. Abaikan ketika localStorage tidak tersedia.
  }
}

const diffTz = 0;

export const getTztime = (date) => {
  if (!date) return null;

  return dayjs(date).subtract(diffTz, 'hours');
};

const OfflineEventInformation = ({ id }) => {
  const navigate = useNavigate();

  const isMobile = useMediaQuery('(max-width:600px)');
  const isTab = useMediaQuery('(max-width:1000px)');

  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isOpenSummary, setIsOpenSummary] = useState(false);
  const [showCompletePopup, setShowCompletePopup] = useState(false);
  const [isBuyInsurance, setIsBuyInsurance] = useState(0);

  const [tabIdx, setTabIdx] = useState(0);
  const [dp, setDp] = useState();
  const [selectedCategory, setSelectedCategory] = useState();

  const [loadOrderAndGetSnapToken, setLoadOrderAndGetSnapToken] =
    useState(false);

  const [isFetchingCodeStatus, setIsFetchingCodeStatus] = useState(false);

  const [chartUrl, setChartUrl] = useState();

  const [code, setCode] = useState({
    value: '',
    errorMessage: '',
    data: {},
  });

  // Checkbox dan dialog peraturan.
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);

  const [isTermsScrolledToBottom, setIsTermsScrolledToBottom] = useState(false);

  const termsContentRef = useRef(null);

  /*
   * Lock synchronous untuk mencegah double-click.
   *
   * State React tidak langsung berubah pada event yang sama.
   * Karena itu, hanya mengandalkan loadOrderAndGetSnapToken
   * masih memungkinkan dua request terkirim secara bersamaan.
   */
  const paymentRequestLockRef = useRef(false);

  // Data event dari API.
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  const numericEventId = Number(id);
  const isWHM = numericEventId === 90 || numericEventId === 91;

  useEffect(() => {
    if (!isTermsDialogOpen) return;

    setIsTermsScrolledToBottom(false);

    requestAnimationFrame(() => {
      const element = termsContentRef.current;

      if (!element) return;

      /*
       * Apabila isi dialog tidak lebih tinggi daripada container,
       * tombol Agree langsung dapat diaktifkan.
       */
      if (element.scrollHeight <= element.clientHeight + 1) {
        setIsTermsScrolledToBottom(true);
      }
    });
  }, [isTermsDialogOpen]);

  useEffect(() => {
    const getOfflaneEventInformation = async () => {
      setIsFetching(true);
      setError(null);

      try {
        const response = await api.get(`/home/offline/event/${id}`);

        setData(response.data);

        /*
         * Tetap simpan cache apabila diperlukan oleh flow lain.
         * Error localStorage tidak akan menghentikan halaman.
         */
        writeCache(id, response.data);
      } catch (requestError) {
        console.error('Failed to get offline event information:', requestError);

        /*
         * Cache hanya digunakan sebagai fallback.
         */
        const cached = readCache(id);

        if (cached?.data) {
          setData(cached.data);
          return;
        }

        setError(requestError);
      } finally {
        setIsFetching(false);
      }
    };

    getOfflaneEventInformation();
  }, [id]);

  const eventId = Number(data?.info?.eventId);

  const isTermsRequired = eventId === 82 || eventId === 84 || eventId === 87;

  useEffect(() => {
    const racepacks = data?.racepacks;

    if (!Array.isArray(racepacks) || racepacks.length === 0) {
      setChartUrl(undefined);
      return;
    }

    const sizeChart = racepacks.find((racepack) =>
      String(racepack?.name || '')
        .toLowerCase()
        .includes('chart'),
    );

    setChartUrl(sizeChart?.image);
  }, [data?.racepacks]);

  const handleCodeDataChange = (key, item) => {
    setCode((previousCode) => ({
      ...previousCode,
      [key]: item,
    }));
  };

  const openTermsDialog = () => {
    setIsTermsDialogOpen(true);
  };

  const handleTermsScroll = (event) => {
    const target = event.currentTarget;

    const hasReachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 8;

    if (hasReachedBottom) {
      setIsTermsScrolledToBottom(true);
    }
  };

  const handleTermsAgree = () => {
    setIsTermsChecked(true);
    setIsTermsDialogOpen(false);
  };

  /*
   * Mengubah berbagai bentuk error menjadi string yang aman
   * untuk ditampilkan melalui alert().
   */
  const getErrorMessage = (
    requestError,
    fallbackMessage = 'Terjadi kesalahan. Silakan coba kembali.',
  ) => {
    const response = requestError?.response;
    const responseData = response?.data;

    const responseText =
      typeof responseData === 'string' ? responseData.trim().toLowerCase() : '';

    /*
     * AxiosHeaders pada versi Axios terbaru memakai .get().
     * Tetap sediakan fallback untuk object headers biasa.
     */
    const contentType =
      response?.headers?.get?.('content-type') ||
      response?.headers?.['content-type'] ||
      '';

    /*
     * Server API mati/tidak terhubung, timeout,
     * atau request malah dijawab server frontend/proxy
     * menggunakan halaman HTML.
     */
    const isServerCannotBeReached =
      !response ||
      navigator.onLine === false ||
      requestError?.code === 'ERR_NETWORK' ||
      requestError?.code === 'ECONNABORTED' ||
      requestError?.code === 'ETIMEDOUT' ||
      requestError?.message === 'Network Error' ||
      contentType.toLowerCase().includes('text/html') ||
      responseText.includes('<!doctype html') ||
      responseText.includes('<html') ||
      responseText.includes('cannot get /api/') ||
      responseText.includes('cannot post /api/') ||
      response?.status === 502 ||
      response?.status === 503 ||
      response?.status === 504;

    if (isServerCannotBeReached) {
      return 'Server cannot be reached. Please try again later.';
    }

    /*
     * Error JSON valid dari backend tetap ditampilkan,
     * misalnya email sudah terdaftar atau kode tidak valid.
     */
    if (
      typeof responseData?.message === 'string' &&
      responseData.message.trim() !== ''
    ) {
      return responseData.message;
    }

    if (
      typeof responseData?.error === 'string' &&
      responseData.error.trim() !== ''
    ) {
      return responseData.error;
    }

    /*
     * String biasa dari backend boleh ditampilkan
     * selama bukan HTML.
     */
    if (
      typeof responseData === 'string' &&
      responseData.trim() !== '' &&
      !responseData.includes('<')
    ) {
      return responseData;
    }

    return fallbackMessage;
  };

  const handleJoinEvent = async () => {
    if (isFetchingCodeStatus) return;

    setIsFetchingCodeStatus(true);

    try {
      if (!selectedCategory) {
        throw new Error('Silakan pilih kategori terlebih dahulu.');
      }

      if (isTermsRequired && !isTermsChecked) {
        throw new Error(
          'Silakan baca dan setujui peraturan terlebih dahulu sebelum join event.',
        );
      }

      const trimmedCode = String(code?.value || '').trim();

      if (!trimmedCode) {
        throw new Error('Silakan masukkan kode.');
      }

      if (!data?.info?.eventId) {
        throw new Error(
          'Informasi event belum tersedia. Silakan refresh halaman lalu coba kembali.',
        );
      }

      const couponResponse = await api.get(
        `/coupon/validate/${data.info.eventId}/${encodeURIComponent(
          trimmedCode,
        )}/${selectedCategory.categoryId}`,
      );

      const couponData = couponResponse?.data;

      if (!couponData) {
        throw new Error(
          'Kode tidak dapat divalidasi. Silakan periksa kembali kode Anda.',
        );
      }

      handleCodeDataChange('data', couponData);

      const params = {
        eventId: data.info.eventId,
        category: {
          race_category: selectedCategory.categoryId,
          name: selectedCategory.name,
          minAge: selectedCategory.adultMinAge,
          maxAllowedDate: selectedCategory.maxAllowedDate,
          adultSizeIndex: selectedCategory.adultSizeIndex,
          endReg: selectedCategory.endDate,
        },
        coupon: {
          voucher: couponData?.code,
          id: couponData?.coupon_id,
        },
      };

      if (Number(data.info.eventId) === 89) {
        const urlParams = generate(JSON.stringify(params));

        navigate(`/event/register/${urlParams}`);

        return;
      }

      setIsOpenDialog(true);
    } catch (requestError) {
      console.error('Join event error:', {
        error: requestError,
        status: requestError?.response?.status,
        response: requestError?.response?.data,
        eventId: data?.info?.eventId,
        categoryId: selectedCategory?.categoryId,
      });

      alert(
        getErrorMessage(
          requestError,
          'Kode tidak dapat diproses. Silakan coba kembali.',
        ),
      );
    } finally {
      setIsFetchingCodeStatus(false);
    }
  };

  const handleOnChange = (event) => {
    try {
      const selectedValue = String(event?.target?.value || '');

      const categoryFind = data?.categories?.find(
        (category) => String(category?.categoryId) === selectedValue,
      );

      if (!categoryFind) {
        throw new Error(
          'Kategori yang dipilih tidak ditemukan. Silakan refresh halaman.',
        );
      }

      setSelectedCategory(categoryFind);

      // Pergantian kategori harus meminta persetujuan ulang.
      setIsTermsChecked(false);
    } catch (changeError) {
      console.error('Category selection error:', changeError);

      alert(getErrorMessage(changeError, 'Kategori tidak dapat dipilih.'));
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIdx(newValue);
  };

  const handleGoToSummary = ({ email, team, participants, emergency }) => {
    try {
      const emailValue = email?.email?.value;

      if (!emailValue) {
        throw new Error(
          'Email pemesan tidak ditemukan. Silakan periksa kembali data registrasi.',
        );
      }

      if (!Array.isArray(participants) || participants.length === 0) {
        throw new Error(
          'Data peserta tidak ditemukan. Silakan isi kembali data peserta.',
        );
      }

      /*
       * Jangan memodifikasi object dp lama secara langsung.
       * Bangun object baru agar state tetap immutable.
       */
      const updated = {
        ...(dp || {}),
        order: {
          email: {
            value: emailValue,
          },
          orderType: {
            value: 'web',
          },
          bill: {
            value: 0,
          },
        },
        team,
        participants,
        emergency,
      };

      setDp(updated);
      setIsOpenDialog(false);

      /*
       * Pastikan percobaan pembayaran baru tidak terblokir
       * oleh request sebelumnya.
       */
      paymentRequestLockRef.current = false;
      setLoadOrderAndGetSnapToken(false);

      setTimeout(() => {
        setIsOpenSummary(true);
      }, 200);
    } catch (summaryError) {
      console.error('Go to summary error:', summaryError);

      alert(
        getErrorMessage(
          summaryError,
          'Ringkasan registrasi tidak dapat dibuka.',
        ),
      );
    }
  };

  const buildRegistrationParams = (bill) => {
    if (!dp || typeof dp !== 'object') {
      throw new Error(
        'Data registrasi belum tersedia. Silakan tutup ringkasan lalu isi ulang data peserta.',
      );
    }

    if (!dp?.order || typeof dp.order !== 'object') {
      throw new Error(
        'Data pesanan tidak lengkap. Silakan tutup ringkasan lalu coba kembali.',
      );
    }

    if (!Array.isArray(dp?.participants) || dp.participants.length === 0) {
      throw new Error(
        'Data peserta tidak ditemukan. Silakan tutup ringkasan lalu isi ulang data peserta.',
      );
    }

    const numericBill = Number(bill);

    if (!Number.isFinite(numericBill) || numericBill < 0) {
      throw new Error('Nominal pembayaran tidak valid.');
    }

    const participantUpdated = dp.participants.map((participant, index) => {
      if (!participant || typeof participant !== 'object') {
        throw new Error(`Data peserta ke-${index + 1} tidak valid.`);
      }

      const birthDateValue =
        participant?.birthDate?.value ?? participant?.birthDate;

      if (!birthDateValue || !dayjs(birthDateValue).isValid()) {
        throw new Error(
          `Tanggal lahir peserta ke-${index + 1} tidak valid. Silakan periksa kembali data peserta.`,
        );
      }

      return {
        ...participant,
        birthDate: dayjs(birthDateValue).format('MM/DD/YYYY'),
      };
    });

    return {
      ...dp,
      order: {
        ...dp.order,
        bill: {
          ...(dp?.order?.bill || {}),
          value: bill,
        },
      },
      participants: participantUpdated,
    };
  };

  const getSelectedCoupon = (coupon) => {
    /*
     * Untuk internal event, gunakan coupon hasil validasi code.
     * Untuk event biasa, gunakan coupon dari summary dialog.
     */
    if (code?.data?.coupon_id) {
      return code;
    }

    return coupon;
  };

  const getCouponPayload = (coupon) => {
    const selectedCoupon = getSelectedCoupon(coupon);

    return {
      id: selectedCoupon?.data?.coupon_id ?? selectedCoupon?.coupon_id ?? null,
      code: selectedCoupon?.data?.code ?? selectedCoupon?.code ?? null,
    };
  };

  const handleChoosePayment = async ({ bill, coupon } = {}) => {
    /*
     * Free registration diproses melalui endpoint berbeda.
     */
    if (Number(bill) === 0) {
      return registerOfflineEvent({
        bill,
        coupon,
      });
    }

    /*
     * Mencegah double-click dan double request.
     */
    if (paymentRequestLockRef.current || loadOrderAndGetSnapToken) {
      return;
    }

    paymentRequestLockRef.current = true;
    setLoadOrderAndGetSnapToken(true);

    try {
      if (!data?.info) {
        throw new Error(
          'Informasi event belum tersedia. Silakan refresh halaman lalu coba kembali.',
        );
      }

      if (!selectedCategory) {
        throw new Error(
          'Kategori belum dipilih. Silakan pilih kategori terlebih dahulu.',
        );
      }

      const endpoint = process.env.REACT_APP_OFFLINE_PAY;

      if (!endpoint) {
        throw new Error(
          'Konfigurasi endpoint pembayaran tidak ditemukan. Hubungi administrator.',
        );
      }

      const params = buildRegistrationParams(bill);
      const couponPayload = getCouponPayload(coupon);

      const apiPayload = {
        event: data.info,
        category: selectedCategory,
        coupon: couponPayload,
        insurance:
          Number(data?.info?.isInsuranceBundled) > 0 ? 1 : isBuyInsurance,
        ...params,
      };

      const result = await api.post(endpoint, apiPayload);

      /*
       * Beberapa backend mengembalikan URL dalam struktur berbeda.
       * Ambil beberapa kemungkinan, tetapi tetap validasi sebelum redirect.
       */
      const paymentUrl =
        result?.data?.url ??
        result?.data?.redirectUrl ??
        result?.data?.redirect_url ??
        result?.data?.paymentUrl ??
        result?.data?.payment_url ??
        result?.data?.data?.url ??
        result?.data?.data?.redirectUrl ??
        result?.data?.data?.redirect_url ??
        result?.data?.data?.paymentUrl ??
        result?.data?.data?.payment_url;

      if (typeof paymentUrl !== 'string' || paymentUrl.trim() === '') {
        console.error('Payment API tidak mengembalikan URL:', {
          status: result?.status,
          response: result?.data,
          eventId: data?.info?.eventId,
          categoryId: selectedCategory?.categoryId,
        });

        throw new Error(
          'URL pembayaran tidak diterima dari server. Silakan coba kembali atau hubungi administrator.',
        );
      }

      const normalizedPaymentUrl = paymentUrl.trim();

      const invalidUrlValues = [
        'undefined',
        'null',
        'false',
        '[object object]',
      ];

      if (invalidUrlValues.includes(normalizedPaymentUrl.toLowerCase())) {
        throw new Error(
          'URL pembayaran dari server tidak valid. Silakan coba kembali atau hubungi administrator.',
        );
      }

      let parsedPaymentUrl;

      try {
        /*
         * window.location.origin sebagai base membuat relative URL
         * tetap dapat digunakan apabila backend memang mengembalikan
         * path dari domain aplikasi.
         */
        parsedPaymentUrl = new URL(
          normalizedPaymentUrl,
          window.location.origin,
        );
      } catch {
        throw new Error(
          'Format URL pembayaran tidak valid. Silakan coba kembali atau hubungi administrator.',
        );
      }

      if (
        parsedPaymentUrl.protocol !== 'https:' &&
        parsedPaymentUrl.protocol !== 'http:'
      ) {
        throw new Error('Protokol URL pembayaran tidak diizinkan.');
      }

      /*
       * Redirect hanya dilakukan setelah URL dipastikan valid.
       */
      window.location.assign(parsedPaymentUrl.href);
    } catch (paymentError) {
      console.error('Choose payment error:', {
        error: paymentError,
        status: paymentError?.response?.status,
        response: paymentError?.response?.data,
        eventId: data?.info?.eventId,
        categoryId: selectedCategory?.categoryId,
      });

      alert(
        getErrorMessage(
          paymentError,
          'Gagal membuka halaman pembayaran. Silakan coba kembali.',
        ),
      );
    } finally {
      /*
       * Apabila redirect berhasil, browser akan meninggalkan halaman.
       * Apabila gagal, tombol kembali dapat digunakan.
       */
      paymentRequestLockRef.current = false;
      setLoadOrderAndGetSnapToken(false);
    }
  };

  const registerOfflineEvent = async ({ bill, coupon } = {}) => {
    if (paymentRequestLockRef.current || loadOrderAndGetSnapToken) {
      return;
    }

    paymentRequestLockRef.current = true;
    setLoadOrderAndGetSnapToken(true);

    try {
      if (!data?.info) {
        throw new Error(
          'Informasi event belum tersedia. Silakan refresh halaman lalu coba kembali.',
        );
      }

      if (!selectedCategory) {
        throw new Error(
          'Kategori belum dipilih. Silakan pilih kategori terlebih dahulu.',
        );
      }

      const endpoint = process.env.REACT_APP_OFFLINE_FREE;

      if (!endpoint) {
        throw new Error(
          'Konfigurasi endpoint registrasi gratis tidak ditemukan. Hubungi administrator.',
        );
      }

      const params = buildRegistrationParams(bill);
      const couponPayload = getCouponPayload(coupon);

      const regParams = {
        event: data.info,
        category: selectedCategory,
        coupon: couponPayload,
        ...params,
      };

      await api.post(endpoint, regParams);

      setIsOpenSummary(false);
      setShowCompletePopup(true);
    } catch (registrationError) {
      console.error('Free registration error:', {
        error: registrationError,
        status: registrationError?.response?.status,
        response: registrationError?.response?.data,
        eventId: data?.info?.eventId,
        categoryId: selectedCategory?.categoryId,
      });

      alert(
        getErrorMessage(
          registrationError,
          'Registrasi gagal diproses. Silakan coba kembali.',
        ),
      );
    } finally {
      paymentRequestLockRef.current = false;
      setLoadOrderAndGetSnapToken(false);
    }
  };

  const getRemainingSlotText = (item) => {
    if (!item) return '-';

    const limit = Number(item.priceLimit);
    const pricePending = Number(item.pricePending);
    const priceSuccess = Number(item.priceSuccess);

    if (
      !Number.isFinite(limit) ||
      !Number.isFinite(pricePending) ||
      !Number.isFinite(priceSuccess)
    ) {
      return '-';
    }

    const priceCount = pricePending + priceSuccess;

    const resultCount = limit - priceCount;

    if (resultCount > 0) {
      const adultsLimit = Number(item.adultsLimit) || 0;

      const kidsLimit = Number(item.kidsLimit) || 0;

      const participantLimit = Number(item.participantLimit) || 0;

      const dividerNumber = adultsLimit + kidsLimit + participantLimit;

      /*
       * Hindari pembagian dengan nol yang menghasilkan Infinity.
       */
      if (dividerNumber <= 0) {
        return resultCount;
      }

      return Math.max(0, Math.floor(resultCount / dividerNumber));
    }

    if (pricePending > 0) {
      return 'Full';
    }

    return 'Sold Out!';
  };

  const checkSlotLimit = (item) => {
    try {
      if (!item) {
        throw new Error('Informasi kategori tidak ditemukan.');
      }

      const limit = Number(item.priceLimit);
      const pricePending = Number(item.pricePending) || 0;

      const priceSuccess = Number(item.priceSuccess) || 0;

      /*
       * Apabila backend tidak memberikan limit,
       * jangan otomatis menganggap sold out.
       */
      if (!Number.isFinite(limit)) {
        return true;
      }

      const resultCount = limit - (pricePending + priceSuccess);

      if (resultCount > 0) {
        return true;
      }

      if (pricePending > 0) {
        throw new Error('Slot Full, please try again later.');
      }

      throw new Error('Ticket Sold Out!');
    } catch (slotError) {
      alert(getErrorMessage(slotError, 'Slot tidak tersedia.'));

      return false;
    }
  };

  const joinEvent = (item) => {
    try {
      if (!item) {
        throw new Error('Silakan pilih kategori terlebih dahulu.');
      }

      if (isTermsRequired && !isTermsChecked) {
        throw new Error(
          'Silakan baca dan setujui peraturan terlebih dahulu sebelum join event.',
        );
      }

      const valid = checkSlotLimit(item);

      if (!valid) return;

      setIsOpenDialog(true);
    } catch (joinError) {
      console.error('Open participant dialog error:', joinError);

      alert(getErrorMessage(joinError, 'Form peserta tidak dapat dibuka.'));
    }
  };

  const onCloseRegCompletePopUp = () => {
    setShowCompletePopup(false);

    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  if (isFetching) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: {
            xs: 8,
            sm: 5,
          },
        }}
      >
        <CircularProgress size="4rem" />
      </Box>
    );
  }

  if (error) {
    const visibleErrorMessage = getErrorMessage(
      error,
      'Event tidak dapat dimuat.',
    );

    return (
      <Box
        display="flex"
        flexGrow={1}
        flex={1}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        sx={{
          height: '500px',
          px: 2,
          textAlign: 'center',
        }}
      >
        <Error
          sx={{
            fontSize: '64px',
            color: '#afafaf',
          }}
        />

        <Typography
          sx={{
            color: '#afafaf',
          }}
          variant="h5"
        >
          {visibleErrorMessage}
        </Typography>

        <Button
          variant="contained"
          sx={{
            mt: 2,
          }}
          onClick={() => window.location.reload()}
        >
          Coba Lagi
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Grid
        container
        style={classes.containerSpaceAround}
        sx={{
          flexDirection: {
            xs: 'column',
            lg: 'row',
          },
          flexWrap: {
            xs: 'wrap',
            sm: 'inherit',
          },
          mt: {
            xs: 3,
            sm: 5,
            xl: 8,
          },
        }}
      >
        {/* Banner event */}
        <Grid item sm={12} lg={5} style={classes.bannerContainer}>
          <img
            src={data?.info?.banner}
            alt={data?.info?.name || 'Event banner'}
            style={
              isMobile
                ? classes.bannerMobile
                : isTab
                  ? classes.bannerMedium
                  : classes.banner
            }
          />
        </Grid>

        {/* Event detail information container */}
        <Grid
          item
          container
          lg={7}
          sx={{
            marginTop: {
              xs: '0',
              sm: '2rem',
              lg: '0',
            },
            paddingInline: {
              xs: '2em',
              sm: '0',
            },
          }}
        >
          {/* Event information detail */}
          <Grid item xs={12} sm={6}>
            <div
              style={{
                display: 'flex',
                flexGrow: 1,
                flexDirection: 'column',
                marginTop: isMobile ? '10px' : '0',
              }}
            >
              <Typography variant={!isMobile ? 'h4' : 'h6'} fontWeight="600">
                {data?.info?.name}
              </Typography>

              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Tabs
                    value={tabIdx}
                    onChange={handleTabChange}
                    aria-label="Event information tabs"
                  >
                    <Tab
                      label={isWHM ? 'Detail' : 'Details'}
                      {...a11lyProps(0)}
                    />

                    <Tab label="Racepacks" {...a11lyProps(1)} />

                    {data?.info?.containInsurance > 0 && (
                      <Tab label="Insurance" {...a11lyProps(2)} />
                    )}
                  </Tabs>
                </Box>

                <CustomTabPanel value={tabIdx} index={0}>
                  <Box>
                    <RaceOrRacepackInfo
                      isMobile={isMobile}
                      label="Race Info"
                      location={data?.info?.eventLocation}
                      start={data?.info?.startDate}
                      end={data?.info?.endDate}
                    />
                  </Box>

                  {id &&
                    numericEventId !== 79 &&
                    numericEventId !== 80 &&
                    numericEventId !== 90 &&
                    numericEventId !== 91 && (
                      <Box>
                        <RaceOrRacepackInfo
                          isMobile={isMobile}
                          label="Racepack Collection Info"
                          location={data?.info?.racepackCollection}
                          start={data?.info?.startRacepackCollectionDate}
                          end={data?.info?.endRacepackCollectionDate}
                          isRacepack
                        />
                      </Box>
                    )}

                  {(numericEventId === 90 || numericEventId === 91) && (
                    <>
                      <Box>
                        <RaceOrRacepackInfo
                          isMobile={isMobile}
                          label="Racepack Collection Info"
                          location={data?.info?.racepackCollection}
                          start="2026-10-02 10:00:00"
                          end="2026-10-02 22:00:00"
                          isRacepack
                        />
                      </Box>

                      <Box>
                        <RaceOrRacepackInfo
                          isMobile={isMobile}
                          label="Racepack Collection Info"
                          location={data?.info?.racepackCollection}
                          start="2026-10-03 10:00:00"
                          end="2026-10-03 20:00:00"
                          isRacepack
                        />
                      </Box>
                    </>
                  )}

                  <Box>
                    <EventInfoFragment
                      isMobile={isMobile}
                      label="Description"
                      body={data?.info?.description}
                      justified
                    />
                  </Box>

                  {(numericEventId === 90 || numericEventId === 91) && (
                    <Box>
                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Race Owner"
                        body="PERHIMPUNAN DOKTER SPESIALIS KEDOKTERAN JIWA INDONESIA (PP PDSKJI)"
                      />
                    </Box>
                  )}

                  <Box>
                    <EventInfoFragment
                      isMobile={isMobile}
                      label="Race Organizer"
                      body={data?.info?.organizer || '-'}
                    />
                  </Box>

                  <Box>
                    <EventInfoFragment
                      isMobile={isMobile}
                      label="Race Management"
                      body={data?.info?.raceManagement || '-'}
                    />
                  </Box>

                  <Box>
                    <EventInfoFragment
                      isMobile={isMobile}
                      label="Timing System"
                      body={data?.info?.timingSystem || '-'}
                    />
                  </Box>
                </CustomTabPanel>

                <CustomTabPanel value={tabIdx} index={1}>
                  <RacepacksFragments racepacks={data?.racepacks} />
                </CustomTabPanel>

                {data?.info?.containInsurance > 0 && (
                  <CustomTabPanel value={tabIdx} index={2}>
                    <Paper
                      style={{
                        maxHeight: '720px',
                        overflowY: 'scroll',
                        padding: '8px',
                      }}
                    >
                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Asuransi di dukung oleh:"
                        body={
                          <img
                            src={prodigiLogo}
                            alt="Prodigi"
                            style={{
                              width: '200px',
                            }}
                          />
                        }
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Nama Produk Asuransi"
                        body="Asuransi Kecelakaan Diri"
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Manfaat"
                        body={
                          <div>
                            1. Meninggal dunia akibat Kecelakaan IDR 50.000.000
                            <br />
                            2. Biaya Ketidakmampuan (Cacat) IDR 50.000.000
                            <br />
                            3. Biaya Pengobatan (termasuk biaya pengobatan
                            alternatif - shinshe berlisensi) IDR 5.000.000
                          </div>
                        }
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Jaminan Asuransi"
                        body={
                          <div>
                            1. Meninggal Dunia Akibat Kecelakaan: Memberikan
                            santunan jika Tertanggung mengalami kecelakaan
                            selama masa pertanggungan asuransi yang menyebabkan
                            kematian. Kecelakaan yang dimaksud adalah semua
                            kejadian yang setara dengan kecelakaan itu sendiri.
                            <br />
                            2. Penggantian Biaya Ketidakmampuan Akibat
                            Kecelakaan saat Olahraga: Memberikan santunan jika
                            Tertanggung mengalami cacat sebagai akibat dari
                            kecelakaan saat melakukan aktivitas olahraga.
                            <br />
                            3. Penggantian Biaya Pengobatan Akibat Kecelakaan
                            saat Olahraga: Memberikan penggantian biaya
                            pengobatan jika Tertanggung mengalami kecelakaan
                            saat melakukan aktivitas olahraga selama masa
                            pertanggungan asuransi.
                            <br />
                            4. Penggantian Biaya Pengobatan Alternatif (Sinshe
                            Berlisensi): Memberikan penggantian biaya perawatan
                            atau pengobatan alternatif sesuai dengan ketentuan
                            polis.
                          </div>
                        }
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Batas Pertanggungan"
                        body={
                          <div>
                            Ketentuan besarnya penggantian maupun santunan:
                            <br />
                            1. Selain manfaat Kematian, semua manfaat lainnya
                            dapat diklaim lebih dari satu kali selama masa
                            pertanggungan dan selama batas santunan masih
                            tersedia.
                            <br />
                            2. Besarnya penggantian risiko Kematian dan Cacat
                            Tetap diberikan sesuai plan yang diambil.
                            <br />
                            3. Penggantian risiko biaya pengobatan di Rumah
                            Sakit sesuai dengan plan dan bukti-bukti pengobatan
                            yang sah.
                          </div>
                        }
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Metode Klaim"
                        body="Santunan / Tebusan (Reimbursement)."
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Kriteria Kelayakan Calon Tertanggung"
                        body={
                          <div>
                            1. Kewarganegaraan: Warga Negara Indonesia dan Warga
                            Negara Asing yang memiliki izin tinggal di
                            Indonesia.
                            <br />
                            2. Usia: 12 tahun – 70 tahun.
                          </div>
                        }
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Syarat & Ketentuan"
                        body={
                          <div>
                            Pengecualian Umum:
                            <br />
                            A. Perang, pemogokan, kerusuhan, pemberontakan,
                            perang saudara, invasi, atau kegiatan perang yang
                            serupa.
                            <br />
                            B. Terorisme yang melibatkan penggunaan bahan
                            nuklir, biologis, atau kimiawi.
                            <br />
                            C. Keterlibatan dalam tugas militer, pelatihan
                            penjinak bom, dan aktivitas serupa.
                            <br />
                            D. Radiasi atau kontaminasi radioaktif.
                            <br />
                            E. Bencana alam.
                            <br />
                            F. Penggunaan alkohol atau obat-obatan secara
                            sengaja, bunuh diri, upaya bunuh diri, atau melukai
                            diri.
                            <br />
                            G. Tindakan melanggar hukum atau paparan disengaja
                            terhadap bahaya yang tidak diperlukan.
                            <br />
                            H. Cedera akibat melakukan tindak kejahatan atau
                            menolak penangkapan.
                            <br />
                            I. Penyakit mental, gangguan mental, dan kelainan
                            bawaan.
                            <br />
                            J. Kehamilan, persalinan, dan komplikasinya.
                            <br />
                            K. Penerbangan selain sebagai penumpang pada
                            penerbangan komersial yang terjadwal.
                            <br />
                            L. Kecelakaan akibat melakukan, mencoba, atau
                            memprovokasi serangan.
                            <br />
                            M. Kecelakaan akibat pekerjaan berisiko tinggi
                            tertentu.
                            <br />
                            N. Pengecualian lain sesuai standar PSAKDI 2016.
                          </div>
                        }
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Penanganan Klaim"
                        body={
                          <div>
                            1. Pelaporan klaim dilakukan sesuai batas waktu yang
                            ditentukan.
                            <br />
                            2. Dokumen klaim harus dilengkapi sesuai ketentuan.
                            <br />
                            3. Penilaian dan akseptasi klaim dilakukan setelah
                            dokumen lengkap.
                            <br />
                            4. Pembayaran manfaat dilakukan setelah Letter of
                            Discharge diterima.
                          </div>
                        }
                      />

                      <EventInfoFragment
                        isMobile={isMobile}
                        label="Cara Klaim"
                        body={
                          <div>
                            1. Pelaporan klaim dapat dilakukan melalui:
                            <p>Akses URL: https://polis.prodiginow.com</p>
                            <p>Atau</p>
                            <p>
                              Claim Hotline: 150118 tekan 2
                              <br />
                              WhatsApp Claim: +6281280489167
                              <br />
                              Jam kerja: Senin–Jumat, 08.00–17.00 WIB
                              <br />
                              Email: claim@prodiginow.com
                            </p>
                            2. Semua dokumen klaim dikirim dalam format digital,
                            termasuk Letter of Discharge yang telah
                            ditandatangani.
                          </div>
                        }
                      />
                    </Paper>
                  </CustomTabPanel>
                )}
              </Box>
            </div>
          </Grid>

          {/* Choose category */}
          <Grid item xs={12} sm={6}>
            <div
              style={{
                display: 'flex',
                marginLeft: isMobile ? '0' : '1rem',
                marginTop: isMobile ? '10px' : '0',
              }}
            >
              <Box
                display="flex"
                justifyContent="flex-start"
                marginBottom="10px"
                flexDirection="column"
                borderRadius="14px"
                padding="10px 16px"
                flex={1}
                sx={{
                  border: 3,
                  borderColor: 'grey.200',
                }}
              >
                {data?.info?.startRegistrationDate &&
                  dayjs().unix() <
                    getTztime(data.info.startRegistrationDate).unix() && (
                    <CountdownTimer
                      openRegDate={getTztime(data.info.startRegistrationDate)}
                    />
                  )}

                {data?.info?.endRegistrationDate &&
                  dayjs().unix() >
                    getTztime(data.info.endRegistrationDate).unix() && (
                    <Typography variant="h4" color="GrayText">
                      Registration Already Closed.
                    </Typography>
                  )}

                {data?.info?.startRegistrationDate &&
                data?.info?.endRegistrationDate &&
                dayjs().unix() >=
                  getTztime(data.info.startRegistrationDate).unix() &&
                dayjs().unix() <=
                  getTztime(data.info.endRegistrationDate).unix() ? (
                  <FormControl>
                    <FormLabel id="choose-category-form">
                      {isWHM ? 'Pilih Kategori' : 'Choose Your Category'}
                    </FormLabel>

                    <RadioGroup
                      aria-labelledby="choose-category-form"
                      name="choose-category-form"
                      value={selectedCategory?.categoryId || ''}
                      onChange={handleOnChange}
                    >
                      {data?.categories?.map((category) => (
                        <FormControlLabel
                          key={category?.categoryId}
                          value={category?.categoryId}
                          control={<Radio />}
                          label={category?.name}
                        />
                      ))}
                    </RadioGroup>

                    {selectedCategory && (
                      <>
                        {Number(data?.info?.isInternal) === 0 && (
                          <Box display="flex" flexDirection="column">
                            <Divider
                              style={{
                                marginBottom: '4px',
                              }}
                            />

                            <Box display="flex" flexDirection="row" gap={1}>
                              <Box
                                display="flex"
                                flexGrow={1}
                                flexDirection="column"
                              >
                                <Typography variant="body1" fontWeight="600">
                                  {isWHM
                                    ? 'Biaya Pendaftaran'
                                    : 'Registration Fee'}
                                </Typography>

                                <Typography variant="body1">
                                  {Number(selectedCategory?.registrationFee) ===
                                  0
                                    ? 'FREE'
                                    : `Rp ${toCurrencyForm(
                                        selectedCategory?.registrationFee,
                                      )}`}
                                </Typography>
                              </Box>

                              <Box
                                display="flex"
                                flexGrow={1}
                                flexDirection="column"
                              >
                                <Typography variant="body1" fontWeight="600">
                                  {isWHM ? 'Grup Harga' : 'Price Group'}
                                </Typography>

                                <Typography variant="body1">
                                  {selectedCategory?.priceLabel || '-'}
                                </Typography>
                              </Box>

                              <Box
                                display="flex"
                                flexGrow={1}
                                flexDirection="column"
                              >
                                <Typography variant="body1" fontWeight="600">
                                  Slot
                                </Typography>

                                <Typography variant="body1">
                                  {getRemainingSlotText(selectedCategory)}
                                </Typography>
                              </Box>
                            </Box>

                            {isTermsRequired && (
                              <Box mt={1}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isTermsChecked}
                                      onChange={(event) =>
                                        setIsTermsChecked(event.target.checked)
                                      }
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      Saya telah membaca dan menyetujui
                                      peraturan yang telah ditentukan.{' '}
                                      <Box
                                        component="span"
                                        sx={{
                                          color: 'error.main',
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                        }}
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          openTermsDialog();
                                        }}
                                      >
                                        Lihat peraturan
                                      </Box>
                                    </Typography>
                                  }
                                />
                              </Box>
                            )}

                            <Button
                              variant="contained"
                              style={{
                                marginTop: '10px',
                              }}
                              onClick={() => joinEvent(selectedCategory)}
                            >
                              {isWHM ? 'Daftar' : 'Join Event!'}
                            </Button>
                          </Box>
                        )}

                        {Number(data?.info?.isInternal) > 0 && (
                          <Box display="flex" flexDirection="column">
                            <Divider
                              style={{
                                marginBottom: '4px',
                              }}
                            />

                            <Box
                              display="flex"
                              flexDirection="row"
                              marginBottom="20px"
                              gap={1}
                            >
                              <Box
                                display="flex"
                                flexGrow={1}
                                flexDirection="column"
                              >
                                <Typography variant="body1" fontWeight="600">
                                  {isWHM
                                    ? 'Biaya Pendaftaran'
                                    : 'Registration Fee'}
                                </Typography>

                                <Typography variant="body1">
                                  {Number(selectedCategory?.registrationFee) ===
                                  0
                                    ? 'FREE'
                                    : `Rp ${toCurrencyForm(
                                        selectedCategory?.registrationFee,
                                      )}`}
                                </Typography>
                              </Box>

                              <Box
                                display="flex"
                                flexGrow={1}
                                flexDirection="column"
                              >
                                <Typography variant="body1" fontWeight="600">
                                  {isWHM ? 'Grup Harga' : 'Price Group'}
                                </Typography>

                                <Typography variant="body1">
                                  {selectedCategory?.priceLabel || '-'}
                                </Typography>
                              </Box>

                              {eventId !== 84 &&
                                eventId !== 87 &&
                                eventId !== 89 && (
                                  <Box
                                    display="flex"
                                    flexGrow={1}
                                    flexDirection="column"
                                  >
                                    <Typography
                                      variant="body1"
                                      fontWeight="600"
                                    >
                                      Slot
                                    </Typography>

                                    <Typography variant="body1">
                                      {getRemainingSlotText(selectedCategory)}
                                    </Typography>
                                  </Box>
                                )}
                            </Box>

                            <FormTextField
                              index={1}
                              i={0}
                              required
                              label={isWHM ? 'Kode' : 'Code'}
                              errorMessage={code.errorMessage}
                              value={code.value}
                              onChange={(event) =>
                                handleCodeDataChange(
                                  'value',
                                  event.target.value,
                                )
                              }
                            />

                            {isTermsRequired && (
                              <Box mb={1}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isTermsChecked}
                                      onChange={(event) =>
                                        setIsTermsChecked(event.target.checked)
                                      }
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      Saya telah membaca dan menyetujui
                                      peraturan yang telah ditentukan.{' '}
                                      <Box
                                        component="span"
                                        sx={{
                                          color: 'error.main',
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                        }}
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          openTermsDialog();
                                        }}
                                      >
                                        Lihat peraturan
                                      </Box>
                                    </Typography>
                                  }
                                />
                              </Box>
                            )}

                            <LoadingButton
                              variant="contained"
                              onClick={handleJoinEvent}
                              loading={isFetchingCodeStatus}
                            >
                              {isWHM ? 'Daftar' : 'Join Event!'}
                            </LoadingButton>
                          </Box>
                        )}
                      </>
                    )}
                  </FormControl>
                ) : null}
              </Box>
            </div>
          </Grid>
        </Grid>
      </Grid>

      <AddDetailParticipantDialog
        eventId={data?.info?.eventId}
        showDialog={isOpenDialog}
        closeDialog={() => setIsOpenDialog(false)}
        categoryId={selectedCategory?.categoryId}
        kidsLimit={selectedCategory?.kidsLimit}
        kidMaxAge={selectedCategory?.kidMaxAge}
        kidSizeRange={selectedCategory?.kidSizeIndex}
        adultsLimit={selectedCategory?.adultsLimit}
        adultMinAge={selectedCategory?.adultMinAge}
        adultMaxAge={selectedCategory?.adultMaxAge}
        maxAllowedDate={selectedCategory?.maxAllowedDate}
        adultSizeRange={selectedCategory?.adultSizeIndex}
        participantLimit={selectedCategory?.participantLimit}
        includeJersey={selectedCategory?.includeJersey}
        isAllSos={selectedCategory?.isAllSos}
        isGroup={selectedCategory?.isGroup}
        goToSummary={handleGoToSummary}
        sizeChartUrl={chartUrl}
        sizesInfo={data?.sizesInfo}
      />

      <TicketSummaryDialog
        showSummary={isOpenSummary}
        onClose={() => {
          if (loadOrderAndGetSnapToken) {
            return;
          }

          setIsOpenSummary(false);
        }}
        event={data?.info}
        team={dp?.team}
        participants={dp?.participants}
        emergency={dp?.emergency}
        category={selectedCategory}
        internalCode={code?.data}
        order={dp?.order}
        onChoosePayment={handleChoosePayment}
        isLoading={loadOrderAndGetSnapToken}
      />

      <RegCompletePopUp
        open={showCompletePopup}
        onClose={onCloseRegCompletePopUp}
      />

      <Dialog
        open={isTermsDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
      >
        <DialogTitle>Peraturan Lomba</DialogTitle>

        <DialogContent
          dividers
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
          }}
          onScroll={handleTermsScroll}
          ref={termsContentRef}
        >
          <Typography variant="body2" paragraph>
            Isi peraturan lomba ditampilkan di sini. Scroll sampai bawah untuk
            mengaktifkan tombol Agree.
          </Typography>

          <Typography variant="body2" paragraph>
            1. Peserta wajib mematuhi seluruh aturan lomba yang ditetapkan oleh
            panitia.
          </Typography>

          <Typography variant="body2" paragraph>
            2. Panitia berhak mendiskualifikasi peserta yang melanggar aturan
            tanpa pengembalian biaya pendaftaran.
          </Typography>

          <Typography variant="body2" paragraph>
            3. Keputusan panitia bersifat final dan tidak dapat diganggu gugat.
          </Typography>

          <Typography variant="body2" paragraph>
            4. Dengan mengikuti lomba ini, peserta menyatakan telah membaca,
            memahami, dan menyetujui seluruh peraturan.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={handleTermsAgree}
            disabled={!isTermsScrolledToBottom}
          >
            Agree
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OfflineEventInformation;
